import { skipToken } from "@reduxjs/toolkit/dist/query";
import {
  MediaQualityOptions,
  UserEntityPreferences,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { useCallback, useMemo } from "react";
import deepmerge from "deepmerge";
import { AxiosResponse } from "axios";
import { SerializedError } from "@reduxjs/toolkit";

type UserEntityPreferencesHookValue = {
  isLoading: boolean;
  isError: boolean;
  preferences?: UserEntityPreferences;
  imageCompressionPreferences: {
    chats: { quality: MediaQualityOptions };
    tasks: { quality: MediaQualityOptions };
    drive: { quality: MediaQualityOptions };
  };
  // TODO: move return type to some utils
  updatePreferences: (newPreferences: UserEntityPreferences) => Promise<
    | {
        data: void;
      }
    | {
        error:
          | AxiosResponse<unknown>
          | {
              __NETWORK_ERROR: boolean;
            }
          | SerializedError;
      }
  >;
};

export const useUserEntityPreferences = ({
  entityId,
  skip,
}: {
  entityId?: string;
  skip?: boolean;
}): UserEntityPreferencesHookValue => {
  const { entitiesApi } = useRestApiProvider();
  const {
    data: preferences,
    isError,
    isLoading,
  } = entitiesApi.useGetUserEntityPreferencesQuery(
    entityId && !skip ? { entityId } : skipToken
  );

  const [getUserEntityPreferences] =
    entitiesApi.useLazyGetUserEntityPreferencesQuery();
  const [updateUserEntityPreferences] =
    entitiesApi.useUpdateUserEntityPreferencesMutation();

  const imageCompressionPreferences = useMemo(
    () => ({
      chats: {
        quality:
          preferences?.media_settings?.image?.chats?.quality || "uncompressed",
      },
      tasks: {
        quality:
          preferences?.media_settings?.image?.tasks?.quality || "uncompressed",
      },
      drive: {
        quality:
          preferences?.media_settings?.image?.drive?.quality || "uncompressed",
      },
    }),
    [preferences?.media_settings?.image]
  );

  const updatePreferences: UserEntityPreferencesHookValue["updatePreferences"] =
    useCallback(
      async (newPreferences) => {
        if (!entityId) {
          throw new Error("No entity selected");
        }
        const mostRecentEntityPreferences = await getUserEntityPreferences(
          {
            entityId,
          },
          false
        );
        if (
          "error" in mostRecentEntityPreferences &&
          !!mostRecentEntityPreferences.error
        ) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw mostRecentEntityPreferences.error;
        }

        const result = await updateUserEntityPreferences({
          entityId,
          data: deepmerge(
            mostRecentEntityPreferences.data || {},
            newPreferences,
            {
              arrayMerge: (_, sourceArray) => sourceArray,
            }
          ),
        });
        if ("error" in result && !!result.error) {
          // eslint-disable-next-line @typescript-eslint/no-throw-literal
          throw result.error;
        }
        return result;
      },
      [entityId, getUserEntityPreferences, updateUserEntityPreferences]
    );

  return {
    preferences,
    updatePreferences,
    isError,
    isLoading,
    imageCompressionPreferences,
  };
};
